import { useMemo } from 'react';
import { useTrip } from '../lib/tripContext.jsx';
import { useTripJson, useWeather } from '../hooks/data.js';

const EXCURSION_ID = 'endicott_arm_cruise';

export default function EndicottArm() {
  const { tripId, trip } = useTrip();
  const data = useTripJson(tripId, 'excursions.json');
  const lat = trip?.destination?.lat ?? 58.3019;
  const lng = trip?.destination?.lng ?? -134.4197;
  const tz = trip?.dates?.timezone ?? 'America/Juneau';
  const weather = useWeather({ lat, lng, hours: 168, timezone: tz });

  const ex = (data.data?.excursions || []).find((e) => e.id === EXCURSION_ID);

  const tripDays = useMemo(() => {
    if (!trip?.dates?.start || !trip?.dates?.end) return [];
    return cruiseDays(trip.dates.start, trip.dates.end, tz);
  }, [trip?.dates?.start, trip?.dates?.end, tz]);

  const scored = useMemo(() => {
    if (!weather.data) return tripDays.map((d) => ({ ...d, score: null }));
    return tripDays.map((d) => ({ ...d, score: scoreCruiseDay(d, weather.data) }));
  }, [tripDays, weather.data]);

  const best =
    scored.find((s) => s.score?.label === 'send it') ||
    scored.find((s) => s.score?.label === 'tolerable') ||
    null;

  return (
    <div className="mx-auto w-full max-w-screen-sm space-y-4 p-4">
      <header>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
          marquee day · the big cruise
        </p>
        <h1 className="mt-1 font-serif text-2xl">Endicott Arm + Dawes Glacier</h1>
        <p className="mt-1 text-sm text-fg-secondary">
          The day everyone remembers. Catamaran south through Stephens Passage to the tidewater
          glacier. Ice-blue water, calving rumbles, harbor seals on bergs.
        </p>
      </header>

      {data.loading && (
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          …loading itinerary
        </p>
      )}
      {data.error && (
        <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
          excursions data unavailable — {data.error.message}
        </div>
      )}

      {ex && (
        <>
          <section className="card overflow-hidden">
            <header className="space-y-1 border-b border-border-tertiary bg-bg-secondary px-4 py-3">
              <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                operator · {ex.operator || 'TBC via aggregator'}
              </p>
              <p className="font-serif text-base">
                ${ex.base_price_per_person} / person · group of 3 ≈ ${ex.group_total_for_3.toLocaleString()}
              </p>
              <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                {ex.duration_hours}h · departs {ex.departs_from}
              </p>
            </header>
            <div className="space-y-3 px-4 py-3">
              {ex.tracy_arm_note && (
                <p className="rounded-md border border-copper/30 bg-copper/10 p-2 text-xs text-copper-dark">
                  <span className="font-mono text-[10px] uppercase tracking-wider">substitute · </span>
                  {ex.tracy_arm_note}
                </p>
              )}
              {ex.weather_note && (
                <p className="rounded-md bg-bg-secondary p-2 text-xs text-fg-secondary">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                    weather note ·{' '}
                  </span>
                  {ex.weather_note}
                </p>
              )}
              {ex.highlights?.length > 0 && (
                <ul className="space-y-1 text-sm text-fg-secondary">
                  {ex.highlights.map((h) => (
                    <li key={h} className="flex gap-2">
                      <span className="text-fg-tertiary" aria-hidden>
                        ◦
                      </span>
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              )}
              {ex.operator_candidates?.length > 0 && !ex.operator && (
                <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                  candidates:{' '}
                  <span className="normal-case tracking-normal text-fg-secondary">
                    {ex.operator_candidates.join(' · ')}
                  </span>
                </p>
              )}
              {ex.booking_url ? (
                <a
                  href={ex.booking_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="block w-full rounded-md border border-action bg-action px-3 py-2 text-center font-mono text-[11px] uppercase tracking-wider text-action-fg transition hover:opacity-90"
                >
                  open booking
                </a>
              ) : (
                <p className="rounded-md border border-dashed border-border-tertiary bg-bg-secondary px-3 py-2 text-center font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                  booking via aggregator · TBC
                </p>
              )}
            </div>
          </section>

          <section
            aria-labelledby="cruise-forecast"
            className="card overflow-hidden"
          >
            <header className="border-b border-border-tertiary bg-bg-secondary px-4 py-3">
              <h2
                id="cruise-forecast"
                className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary"
              >
                day forecast (08:00–18:00 local)
              </h2>
              <p className="mt-1 font-serif text-base">
                {best?.score
                  ? `${best.label} is the best day to go`
                  : 'forecast loads closer to trip dates'}
              </p>
            </header>
            <ul className="divide-y divide-border-tertiary">
              {scored.map((day) => (
                <ForecastRow key={day.iso} day={day} isBest={day === best} />
              ))}
              {scored.length === 0 && (
                <li className="px-4 py-6 text-center text-sm text-fg-secondary">
                  {weather.loading ? '…loading forecast' : 'No trip days configured.'}
                </li>
              )}
            </ul>
            {weather.error && (
              <p className="border-t border-border-tertiary px-4 py-2 font-mono text-[10px] text-danger">
                weather unavailable — {weather.error.message}
              </p>
            )}
          </section>

          {ex.prep_checklist?.length > 0 && (
            <section className="card overflow-hidden">
              <header className="border-b border-border-tertiary bg-bg-secondary px-4 py-3">
                <h2 className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                  prep checklist
                </h2>
              </header>
              <ul className="space-y-1.5 px-4 py-3 text-sm text-fg-secondary">
                {ex.prep_checklist.map((p) => (
                  <li key={p} className="flex gap-2">
                    <span className="text-fg-tertiary" aria-hidden>
                      □
                    </span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function ForecastRow({ day, isBest }) {
  const score = day.score;
  return (
    <li
      className={[
        'flex items-baseline justify-between gap-3 px-4 py-3',
        isBest ? 'bg-action/5' : '',
      ].join(' ')}
    >
      <div className="min-w-0">
        <p className="font-mono text-[11px] uppercase tracking-wider text-fg-secondary">
          {day.label}
          {isBest && (
            <span className="ml-2 rounded-full border border-action/40 bg-action/10 px-1.5 py-0.5 text-[10px] text-action">
              pick this day
            </span>
          )}
        </p>
        {score ? (
          <p className="mt-1 text-sm text-fg-primary">{score.summary}</p>
        ) : (
          <p className="mt-1 font-serif text-sm italic text-fg-tertiary">
            forecast not yet in range
          </p>
        )}
      </div>
      {score && (
        <div className="text-right font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          <p>wind {score.windKt}kt</p>
          <p>cloud {score.cloudPct}%</p>
          <p>precip {score.precipPct}%</p>
        </div>
      )}
    </li>
  );
}

function cruiseDays(startISO, endISO, tz) {
  const start = new Date(`${startISO}T12:00:00Z`);
  const end = new Date(`${endISO}T12:00:00Z`);
  const out = [];
  const day = new Date(start);
  while (day < end) {
    out.push({
      iso: ymdInZone(day, tz),
      label: day.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: tz,
      }),
    });
    day.setUTCDate(day.getUTCDate() + 1);
  }
  return out;
}

function ymdInZone(d, tz) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const y = parts.find((p) => p.type === 'year').value;
  const m = parts.find((p) => p.type === 'month').value;
  const day = parts.find((p) => p.type === 'day').value;
  return `${y}-${m}-${day}`;
}

// Score a full cruise day 08:00–18:00. Endicott Arm is mostly Stephens
// Passage water — wind chop, low ceiling (hides glacier face), and
// precipitation are the killers. Cloud cover only matters above ~70%
// (overcast erases scale on the glacier face).
function scoreCruiseDay(day, weatherData) {
  if (!weatherData?.hourly?.time?.length) return null;
  const startMs = Date.parse(`${day.iso}T08:00:00`);
  const endMs = Date.parse(`${day.iso}T18:00:00`);
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return null;

  const winds = [];
  const clouds = [];
  const precips = [];
  for (let i = 0; i < weatherData.hourly.time.length; i += 1) {
    const t = Date.parse(weatherData.hourly.time[i]);
    if (Number.isNaN(t)) continue;
    if (t < startMs || t > endMs) continue;
    const w = weatherData.hourly.wind_speed_10m?.[i];
    const c = weatherData.hourly.cloud_cover?.[i];
    const p = weatherData.hourly.precipitation_probability?.[i];
    if (typeof w === 'number') winds.push(w);
    if (typeof c === 'number') clouds.push(c);
    if (typeof p === 'number') precips.push(p);
  }
  if (winds.length === 0) return null;

  const avgWindMph = avg(winds);
  const avgCloud = avg(clouds);
  const avgPrecip = avg(precips);

  const windKt = Math.round(avgWindMph * 0.8689);
  const cloudPct = Math.round(avgCloud);
  const precipPct = Math.round(avgPrecip);

  let label;
  if (windKt <= 10 && precipPct <= 20 && cloudPct <= 70) label = 'send it';
  else if (windKt <= 18 && precipPct <= 50 && cloudPct <= 90) label = 'tolerable';
  else label = 'rough';

  const summary =
    label === 'send it'
      ? 'Light winds, clear ceiling, glacier face fully visible.'
      : label === 'tolerable'
        ? 'Some weather; the catamaran handles it. Pack layers.'
        : 'Wind, rain, or low ceiling will cap the experience.';

  return { label, summary, windKt, cloudPct, precipPct };
}

function avg(arr) {
  if (arr.length === 0) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}
